// ============================================================
// Erscheinungsbild der Kommune im Browser.
//
// - Akzentfarbe: legt die Design-Tokens (--accent …, tokens.css) als Inline-
//   Stil auf <html>. Die Farbe wird fuer weisse Schrift abgedunkelt — viele
//   Stellen setzen `color: #fff` auf var(--accent), ein helles Gelb waere dort
//   unlesbar. Im dunklen Thema bekommt die Schriftvariante (--accent-text)
//   dagegen eine AUFGEHELLTE Fassung, sonst verschwindet sie auf Schwarz.
// - Logo: Adressen, Upload (jede Bilddatei wird zu PNG, weil Excel und PDF
//   nur so einbetten) und Laden fuer den PDF-Export.
//
// Die Farbrechnung entspricht backend/src/data/branding.ts (dort getestet).
// ============================================================

export const STANDARD_AKZENT = "#014351";
const AKZENT_RE = /^#[0-9a-f]{6}$/i;

/** Tokens, die die Kommune ueberschreibt — beim Wechsel werden ALLE entfernt */
const TOKENS = ["--accent", "--accent-2", "--accent-on", "--accent-text", "--accent-soft", "--accent-ring", "--hover", "--hover-strong"] as const;

function kanaele(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function alsHex(r: number, g: number, b: number): string {
  return "#" + [r, g, b].map((v) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, "0")).join("");
}

function relLuminanz(hex: string): number {
  const [r, g, b] = kanaele(hex).map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function kontrast(a: string, b: string): number {
  const [hell, dunkel] = [relLuminanz(a), relLuminanz(b)].sort((x, y) => y - x) as [number, number];
  return (hell + 0.05) / (dunkel + 0.05);
}

export function mischen(hex: string, ziel: string, anteil: number): string {
  const a = kanaele(hex);
  const z = kanaele(ziel);
  return alsHex(a[0] + (z[0] - a[0]) * anteil, a[1] + (z[1] - a[1]) * anteil, a[2] + (z[2] - a[2]) * anteil);
}

/** In Richtung `ziel` verschieben, bis `grund` den geforderten Kontrast erreicht. */
function verschiebenBis(hex: string, ziel: string, grund: string, mindest: number): string {
  for (let anteil = 0; anteil <= 1; anteil += 0.05) {
    const f = mischen(hex, ziel, anteil);
    if (kontrast(f, grund) >= mindest) return f;
  }
  return ziel;
}

/** Fuer weisse Schrift lesbar (4,5:1) — so erscheint die Farbe auf Knoepfen und Kopfzeilen. */
export function lesbarAufWeiss(hex: string): string {
  return verschiebenBis(hex, "#000000", "#ffffff", 4.5);
}

function rgba(hex: string, alpha: number): string {
  const [r, g, b] = kanaele(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function istAkzent(v: unknown): v is string {
  return typeof v === "string" && AKZENT_RE.test(v);
}

/** Token-Werte fuer eine Kommunenfarbe — dieselben Anteile wie tokens.css beim Petrol. */
export function akzentVariablen(hex: string, dunkel: boolean): Record<string, string> {
  const accent = lesbarAufWeiss(hex);
  if (!dunkel) {
    return {
      "--accent": accent,
      "--accent-2": mischen(accent, "#ffffff", 0.14),
      "--accent-on": "#ffffff",
      "--accent-text": accent,
      "--accent-soft": rgba(accent, 0.1),
      "--accent-ring": rgba(accent, 0.22),
      "--hover": rgba(accent, 0.06),
      "--hover-strong": rgba(accent, 0.1),
    };
  }
  // Grund des dunklen Themas (--bg) ist #070707; Schrift braucht dort deutlich mehr Helligkeit
  const text = verschiebenBis(hex, "#ffffff", "#070707", 6);
  return {
    "--accent": accent,
    "--accent-2": mischen(accent, "#ffffff", 0.12),
    "--accent-on": "#ffffff",
    "--accent-text": text,
    "--accent-soft": rgba(text, 0.14),
    "--accent-ring": rgba(text, 0.3),
  };
}

/** Kommunenfarbe auf die Oberflaeche legen; null/ungueltig = zurueck zum Standard. */
export function wendeAkzentAn(hex: string | null | undefined, dunkel: boolean): void {
  const stil = document.documentElement.style;
  for (const t of TOKENS) stil.removeProperty(t);
  if (!istAkzent(hex)) return;
  for (const [k, v] of Object.entries(akzentVariablen(hex, dunkel))) stil.setProperty(k, v);
}

// ---------------- Logo ----------------

/** Logo eines Projekts; `v` = logoSig aus der Projektantwort (erlaubt dauerhaftes Zwischenspeichern). */
export function logoAdresse(projektId: string, v?: string | null): string {
  return `/api/projects/${encodeURIComponent(projektId)}/logo${v ? `?v=${encodeURIComponent(v)}` : ""}`;
}

/** Obergrenze wie im Backend (LOGO_MAX_ZEICHEN) */
const LOGO_MAX_ZEICHEN = 1_500_000;

function bildLaden(src: string): Promise<HTMLImageElement> {
  return new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = () => rej(new Error("Bild konnte nicht geladen werden"));
    img.src = src;
  });
}

/**
 * Bilddatei (PNG, JPEG, WebP, SVG) → PNG als data:-URL, laengste Kante hoechstens
 * 800 px. Transparenz bleibt erhalten. Wird es zu gross, geht es kleiner weiter.
 */
export async function dateiAlsLogoPng(file: File): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = await bildLaden(url);
    // SVG ohne eigene Masse meldet 0 — dann ein Querformat annehmen
    const w0 = img.naturalWidth || 800;
    const h0 = img.naturalHeight || 400;
    for (const kante of [800, 500, 320]) {
      const f = Math.min(1, kante / Math.max(w0, h0)) || 1;
      const c = document.createElement("canvas");
      c.width = Math.max(1, Math.round(w0 * f));
      c.height = Math.max(1, Math.round(h0 * f));
      const ctx = c.getContext("2d")!;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, 0, 0, c.width, c.height);
      const png = c.toDataURL("image/png");
      if (png.length <= LOGO_MAX_ZEICHEN) return png;
    }
    throw new Error("Das Logo ist auch verkleinert noch zu groß — bitte eine einfachere Datei verwenden.");
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Logo fuer den PDF-Export: als data:-URL samt Seitenverhaeltnis; null, wenn keins da ist. */
export async function logoFuerPdf(adresse: string): Promise<{ dataUrl: string; breite: number; hoehe: number } | null> {
  try {
    const antwort = await fetch(adresse, { credentials: "same-origin" });
    if (!antwort.ok) return null;
    const blob = await antwort.blob();
    const dataUrl = await new Promise<string>((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(String(r.result));
      r.onerror = () => rej(r.error);
      r.readAsDataURL(blob);
    });
    const img = await bildLaden(dataUrl);
    return { dataUrl, breite: img.naturalWidth || 1, hoehe: img.naturalHeight || 1 };
  } catch {
    return null;
  }
}
